import {
  API,
  BuilderPlatformInfo,
  DynamicValueService,
  PipeRegistry,
  PlatformBuilder,
  ProductPartViewNode,
  SceneSpecificationBuilder,
  View3dConfiguration,
  View3dConfigurationDto,
  View3dFactory,
} from '@canvas-logic/clp-viewer3d';

import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { NotificationService } from '../services/NotificationService';
import Localization, { Locale } from '../services/Localization';
import { CompositeApiSdk } from '@/types';
import { createConfiguratorContainerUIElementState, createConfiguratorUIElementState } from '@/stores/utils';
import { ChangeComponentAttributesVisibilityEvent, UpdateDependentAttributesEvent } from '@/stores/events';
import {
  CfgUIElementState,
  ConfiguratorUIElementStates,
  ConfiguratorUIElementWithChildrenState,
} from '@/stores/CfgUIElementState';
import { IPrice, PartsRegistry, ralToHex } from '@canvas-logic/clp-domain';
import {
  IConfiguratorCatalogProductApiOptions,
  IConfiguratorUIDto,
  IUiCfgContainer,
  IUiCfgContainerItem,
} from '@canvas-logic/clp-api-types';
import { PdfService } from '@/ui-kit/services/PdfService';
import { OptionsConfiguratorUIStore } from '@/stores/OptionsConfiguratorUIStore';
import { LookupStorage } from '@/ui-kit/domain/LookupStorage';
import { FetcherApi, HqrImageResolver } from '@canvas-logic/clp-sdk';
import { IHqrConfiguratorDto } from '@canvas-logic/clp-domain';
import { ContactUsService } from '@/ui-kit/services/ContactUsService';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { addSearchParamToUrl } from '@/helpers/url';
import { ConfiguratorParams } from '@/constants';
import { AttributeCfgUIElementStateBase } from '@/stores/AttributeCfgUIElementStateBase';
import { prepareUILayout } from '@/ui-kit/domain/virtualLayoutBuilder';

const SKIP_RENDERING = false;

const traversChildren = (item: ConfiguratorUIElementStates, fn: (it: ConfiguratorUIElementStates) => void) => {
  fn(item);
  if ('children' in item) {
    for (const child of item.children) {
      traversChildren(child, fn);
    }
  }
};

export interface Basic3dLayoutParams {
  configuratorUI: IConfiguratorUIDto;
  view3d: View3dConfigurationDto;
  sdk: CompositeApiSdk;
  projectId: number;
  productId: number;
  draft: boolean;
  params: IConfiguratorCatalogProductApiOptions;
  fetcher: FetcherApi;
  without3d?: boolean;
}

export class Bootstrapper {
  private readonly view3dFactory: View3dFactory;
  public api!: API;
  private pdfService!: PdfService;
  private sceneSpecificationBuilder!: SceneSpecificationBuilder;
  #lookupStorage?: LookupStorage;
  contactUsService: ContactUsService | null = null;
  showPrice: boolean = false;
  showRuler = false;
  hqrConfig: IHqrConfiguratorDto | null = null;
  rootElement: ConfiguratorUIElementWithChildrenState;

  optionsViewStore: OptionsConfiguratorUIStore;

  productVariant: {};
  configuratorUI: IConfiguratorUIDto;
  view3dConfigurationConfig: View3dConfigurationDto;

  localization: Localization;
  notificationService: NotificationService;
  layoutRoot: IUiCfgContainer;

  readonly sdk: CompositeApiSdk;
  totalPrice: IPrice = {};
  private readonly productId: number;
  public readonly projectId: number;
  private readonly draft: boolean;
  private readonly isCatalog: boolean = false;
  private elements: CfgUIElementState[] = [];
  private builder!: PlatformBuilder;
  private context!: BuilderPlatformInfo;

  get catalogId(): number {
    if (!this.params.params.catalogId) {
      throw new Error('Not supported');
    }
    return this.params.params.catalogId;
  }

  constructor(private readonly canvas: HTMLCanvasElement, private readonly params: Basic3dLayoutParams) {
    this.isCatalog = !!params.params.catalogId;

    if (params.params.catalogId) {
      this.contactUsService = new ContactUsService(params.params, params.fetcher);

      params.fetcher.getPricingFeatureSchema(params.params).then((schema) => {
        this.showPrice = schema.enabled;
      });
    }
    this.optionsViewStore = new OptionsConfiguratorUIStore();
    if (!params.without3d) {
      this.view3dFactory = new View3dFactory(canvas);
    }

    this.sdk = params.sdk;
    this.configuratorUI = params.configuratorUI;
    this.productId = params.productId;
    this.projectId = params.projectId;
    this.draft = params.draft;
    if (!this.sdk.composite) {
      throw new Error('Not supported');
    }
    this.buildLayout();

    this.view3dConfigurationConfig = params.view3d;
    this.localization = new Localization(params.configuratorUI.localization, Locale.en);
    this.notificationService = new NotificationService();
    if (this.sdk.composite) {
      this.#lookupStorage = new LookupStorage(this.sdk.api);
    }

    this.subscribeToAutoCorrection();
    this.updateAllAttributes();
    makeObservable(this, {
      productVariant: observable,
      totalPrice: observable,
      showRuler: observable,
      showPrice: observable,
      hqrConfig: observable,
      hqrImageUrl: computed,
      handleProductChanges: action.bound,
      addToCart: action.bound,
      switchRuler: action.bound,
      createLink: action.bound,
      buildLayout: action.bound,
      configuratorUI: observable,
    });

    // Load HQR config once on init
    this.loadHqrConfig();
    this.updateTotalPrice();
    embeddingService.subscribeOnLayoutConfigChange((layoutConfig) => {
      this.updateLayoutConfigSettings({ ...layoutConfig.payload });
    }, []);
  }

  buildLayout() {
    this.productVariant = this.sdk.api.getVariant(); // params.configuration;
    this.layoutRoot = prepareUILayout(this.configuratorUI.layout.layout);
    const traversChildren = (item: IUiCfgContainerItem, parent: IUiCfgContainer, prevItem?: IUiCfgContainerItem) => {
      if (item.kind === 'container') {
        const children: CfgUIElementState[] = [];
        let prev: IUiCfgContainerItem | undefined;
        for (const child of item.children) {
          children.push(traversChildren(child, item, prev));
          prev = child;
        }

        const elementState = createConfiguratorContainerUIElementState(this.sdk.api, item, children);
        this.elements.push(elementState);

        return elementState;
      } else {
        const elementState = createConfiguratorUIElementState(this.sdk.api, item, parent);

        elementState.addEventListener((payload) => {
          if (payload.event === 'changeComponentAttributesVisibility') {
            this.handleComponentAttributesVisibilityChange(payload);
          }
          if (payload.event === 'updateDependentAttributes') {
            this.handleUpdateDependentAttributes(payload);
          }
        });
        this.elements.push(elementState);
        return elementState;
      }
    };

    this.rootElement = traversChildren(this.layoutRoot) as ConfiguratorUIElementWithChildrenState;
  }

  updateLayoutConfigSettings(layout: IConfiguratorUIDto) {
    this.configuratorUI = layout;
    this.buildLayout();
  }

  get lookupStorage() {
    if (!this.#lookupStorage) {
      throw new Error('Lookup storage is not initialized');
    }
    return this.#lookupStorage;
  }

  private handleComponentAttributesVisibilityChange(payload: ChangeComponentAttributesVisibilityEvent) {
    traversChildren(this.rootElement, (element) => {
      if (
        element instanceof AttributeCfgUIElementStateBase &&
        element.componentKey === payload.componentKey &&
        element.id !== payload.target.id
      ) {
        element.setVisibility(payload.visible);
      }
    });
    this.handleProductChanges();
  }

  get isComposite(): boolean {
    return this.sdk.composite;
  }

  get isInCatalog(): boolean {
    return this.sdk.composite && this.sdk.api.isInCatalog;
  }

  get hasPrice(): boolean {
    return this.showPrice && !!Object.keys(this.totalPrice).length;
  }

  /**
   * Computed property that returns the HQR image URL for the current configuration.
   * Returns null if HQR is not configured or no image matches the current variant.
   * Depends on productVariant observable so it recalculates when variant changes.
   */
  get hqrImageUrl(): string | null {
    if (!this.hqrConfig || !this.hqrConfig.enabled || !this.sdk.composite) {
      return null;
    }

    // Use this.productVariant (observable) so MobX tracks the dependency
    // and recalculates when variant changes
    const currentVariant = this.productVariant;
    if (!currentVariant || Object.keys(currentVariant).length === 0) {
      return this.hqrConfig.defaultImage;
    }

    const resolver = new HqrImageResolver(this.hqrConfig.config);
    const resolvedImage = resolver.resolve(currentVariant);

    if (resolvedImage) {
      return resolvedImage;
    }

    return this.hqrConfig.defaultImage;
  }

  /**
   * Load HQR configuration from API (called once on init)
   */
  async loadHqrConfig(): Promise<void> {
    try {
      const config = await this.params.fetcher.getHqrConfig(this.params.params);
      runInAction(() => {
        this.hqrConfig = config;
      });
    } catch (error) {
      console.warn('Failed to load HQR config:', error);
      runInAction(() => {
        this.hqrConfig = null;
      });
    }
  }

  /**
   * Fetch HQR (High Quality Rendering) configuration from the API
   * @returns HQR configuration DTO with enabled flag, default image, and config entries
   * @deprecated Use loadHqrConfig() to load once on init and hqrImageUrl to get current image
   */
  async getHqrConfig(): Promise<IHqrConfiguratorDto> {
    return this.params.fetcher.getHqrConfig(this.params.params);
  }

  /**
   * Resolve HQR image URL for the current product variant
   * @param hqrConfig The HQR configuration from getHqrConfig()
   * @returns The resolved image URL or null if no match
   * @deprecated Use hqrImageUrl computed property instead
   */
  resolveHqrImage(hqrConfig: IHqrConfiguratorDto): string | null {
    if (!hqrConfig.enabled || !this.sdk.composite) {
      return null;
    }

    const resolver = new HqrImageResolver(hqrConfig.config);
    const currentVariant = this.sdk.api.getVariant();
    const resolvedImage = resolver.resolve(currentVariant);

    if (resolvedImage) {
      return resolvedImage;
    }

    return hqrConfig.defaultImage;
  }

  private subscribeToAutoCorrection() {
    // TODO: Add to SDK
    // this.engine.notifications.subscribe<LookupMutatorCorrectionEvent>(LookupMutator.CorrectionEvent, (event) => {
    //   const translator = new OptionsCorrectionsTranslator(this.localization);
    //   const messages = translator.translate(event.optionsCorrections);
    //   if (messages.length) {
    //     this.notificationService.info(messages);
    //   }
    // });
  }

  get skipRendering() {
    return SKIP_RENDERING || this.params.without3d;
  }

  async init() {
    if (!this.skipRendering) {
      const pipeRegistry = new PipeRegistry();
      pipeRegistry.registerPipe('ralToRgb', ralToHex);
      const dynamicValueService = new DynamicValueService(pipeRegistry);
      let view3dConfiguration = new View3dConfiguration(this.view3dConfigurationConfig);
      const partsRegistry = new PartsRegistry<ProductPartViewNode>();
      this.context = new BuilderPlatformInfo(this.view3dConfigurationConfig, dynamicValueService, partsRegistry);
      this.builder = new PlatformBuilder(this.context);
      this.api = await this.view3dFactory.createV2(view3dConfiguration, this.builder);

      if (this.isCatalog) {
        this.pdfService = new PdfService(this.sdk, this.api, {
          catalogId: this.catalogId,
          draft: this.draft,
        });
      }

      // this.sceneSpecificationBuilder = new SceneSpecificationBuilder(dynamicValueService, view3dConfiguration);
      // const sceneSpecification = this.sceneSpecificationBuilder.buildSpec(toJS(this.productVariant));

      this.context.setProduct(toJS(this.productVariant));
      await this.api.showSceneV2();

      await this.sdk.api.awaitLogicEngine();
      const { isValid, invalidRules } = this.sdk.api.validate({ ignoreAllRules: false });
      if (!isValid) {
        console.error('Invalid rules', invalidRules);
        let message = 'Invalid configuration';
        if (invalidRules.length > 0) {
          message += `. Check rules: ${invalidRules.join(', ')}`;
        }
        alert(message);
      }
    }
  }

  async addToCart() {
    if (!this.sdk.composite) {
      throw new Error('Not supported');
    }
    const items = await this.sdk.api.getMetaIds();
    const msg = { type: 'add', items };
    const isIframe = window !== window.parent.window;
    if (isIframe && items.length > 0) {
      window.parent.postMessage(msg, '*');
    }
  }

  handleProductChanges() {
    this.productVariant = this.sdk.composite ? this.sdk.api.getVariant() : this.sdk.api.getVariant();
    this.updateTotalPrice();
    this.notifyHostPage();
    if (!this.skipRendering) {
      // const sceneSpecification = this.sceneSpecificationBuilder.buildSpec(toJS(this.productVariant));
      // this.builder.update(toJS(this.productVariant));
      this.context.setProduct(toJS(this.productVariant));
      this.api.showSceneV2();
    }

    this.lookupStorage.update();
    this.updateAllAttributes();
  }

  private updateTotalPrice() {
    if (this.isCatalog) {
      this.sdk.api.getPrice().then((price) => runInAction(() => (this.totalPrice = price)));
    }
  }

  dispose() {
    this.api?.dispose();
  }

  private notifyHostPage() {
    if (this.sdk.composite) {
    }
  }

  private handleUpdateDependentAttributes(payload: UpdateDependentAttributesEvent) {
    this.handleProductChanges();
  }

  private updateAllAttributes() {
    this.elements.forEach((element) => {
      element.update();
    });
  }

  async createLink(): Promise<string> {
    const shortLink = this.sdk.composite ? await this.sdk.api.createLink() : '';
    if (embeddingService.isEmbedded) {
      return await this.createEmbeddedLink(shortLink);
    }
    return addSearchParamToUrl(ConfiguratorParams.link, shortLink);
  }

  async downloadPdf(link: string) {
    await this.pdfService.downloadPdf(link);
  }

  switchRuler(on?: boolean) {
    const newValue = on ?? !this.showRuler;

    if (newValue) {
      this.api.showRuler();
      this.showRuler = true;
    } else {
      this.api.hideRuler();
      this.showRuler = false;
    }
  }

  private async createEmbeddedLink(shortLink: string) {
    const location = await embeddingService.getLocation();
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(ConfiguratorParams.link, shortLink);
    const search = `?${queryParams}`;
    const hash = `#configuratorIframe`;
    return location.origin + location.pathname + search + hash;
  }
}
